<template>
    <div ref="container" class="custom-image-viewer">
        <v-stage v-show="!isLoading" ref="stage" :config="stageConfig">
            <v-layer>
                <v-image ref="image" :config="imageConfig" />
            </v-layer>
        </v-stage>
        <div v-show="isLoading">
            <b-spinner variant="secondary" small />
        </div>
    </div>
</template>

<script>
export default {
    props: {
        url: {
            type: String,
            required: true,
        },
        minZoom: {
            type: Number,
            default: 1,
        },
        maxZoom: {
            type: Number,
            default: 100,
        },
        zoomFactor: {
            type: Number,
            default: 1.25,
        },
        // Will reset image position when zoom reaches minimum value
        minZoomReset: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            isLoading: false,
            stageConfig: {},
            stage: {},
            mainLayer: {},
            imageConfig: {},
            image: null,
            imageNode: {},
            scale: 1,
            lastPinchDistance: null,
            originalImageHeight: null,
            originalImageWidth: null,
        }
    },
    methods: {
        // STAGE
        configStage() {
            let { clientWidth, clientHeight } = this.$refs.container
            this.stageConfig = {
                width: clientWidth,
                height: clientHeight,
            }
            this.stage = this.$refs.stage.getStage()
        },

        // RESIZE
        resizeStage() {
            if (this.$refs.container != undefined) {
                let { clientWidth, clientHeight } = this.$refs.container
                this.stage.width(clientWidth)
                this.stage.height(clientHeight)
                this.stage.draw()
            }
        },
        resizeImage() {
            if (this.$refs.container != undefined) {
                let scale = 1
                let { clientWidth, clientHeight } = this.$refs.container
                let { width, height } = this.image
                if (width * scale > clientWidth) {
                    scale = clientWidth / width
                }
                if (height * scale > clientHeight) {
                    scale = clientHeight / height
                }
                this.imageNode.scale({ x: scale, y: scale })
                this.imageNode.position({
                    x: (clientWidth - width * scale) / 2,
                    y: (clientHeight - height * scale) / 2,
                })
            }
        },
        resize() {
            this.resizeStage()
            this.resizeImage()
        },

        // IMAGE
        async loadImage() {
            return new Promise((resolve) => {
                const image = new Image()
                image.src = this.url
                image.onload = () => {
                    this.image = image
                    this.originalImageWidth = image.width
                    this.originalImageHeight = image.height
                    resolve()
                }
            })
        },
        configImage() {
            let imgDimensions = this.getImageDimensions()

            this.imageConfig = {
                image: this.image,
                draggable: true,
                opacity: 1,
                width: imgDimensions.width,
                height: imgDimensions.height,
                scale: { x: imgDimensions.scale, y: imgDimensions.scale },
                position: {
                    x: imgDimensions.x,
                    y: imgDimensions.y,
                },
            }
            this.cacheImageNode()
        },
        cacheImageNode() {
            this.imageNode = this.$refs.image.getStage()
            this.mainLayer = this.imageNode.getLayer()

            this.stage.batchDraw()
        },

        // ZOOM
        configZoom() {
            this.stage.on('wheel', (e) => {
                e.evt.stopPropagation()

                let pointer = this.stage.getPointerPosition()
                if (e.evt.deltaY < 0) {
                    this.scaleUp(pointer)
                } else {
                    this.scaleDown(pointer)
                }
            })

            this.stage.on('touchmove', (e) => {
                if (e.evt.touches && e.evt.touches.length > 1) {
                    e.evt.preventDefault()
                    e.evt.stopPropagation()

                    let firstTouch = e.evt.touches[0]
                    let secondTouch = e.evt.touches[1]
                    let distance = this.getPinchDistance({ x: firstTouch.clientX, y: firstTouch.clientY }, { x: secondTouch.clientX, y: secondTouch.clientY })

                    if (!this.lastPinchDistance) {
                        this.lastPinchDistance = distance
                    }

                    let scale = (this.scale * distance) / (this.lastPinchDistance * 2)
                    this.scaleCanvas(scale, {
                        x: this.stageConfig.width / 2,
                        y: this.stageConfig.height / 2,
                    })
                }
            })

            this.stage.on('touchend', (e) => {
                this.lastPinchDistance = null
            })
        },
        zoomIn() {
            let pointer = { x: this.stageConfig.width / 2, y: this.stageConfig.height / 2 }
            this.scaleUp(pointer)
        },
        zoomOut() {
            let pointer = { x: this.stageConfig.width / 2, y: this.stageConfig.height / 2 }
            this.scaleDown(pointer)
        },
        scaleUp(pointer) {
            let scale = this.stage.scaleX() * this.zoomFactor
            this.scaleCanvas(scale, pointer)
        },
        scaleDown(pointer) {
            let scale = this.stage.scaleX() / this.zoomFactor
            this.scaleCanvas(scale, pointer)
        },
        scaleCanvas(scale, pointer) {
            let currentScale = this.stage.scaleX()
            let pos = { x: 0, y: 0 }

            if (scale > this.maxZoom) {
                scale = this.maxZoom
            }

            if (scale < this.minZoom) {
                scale = this.minZoom
                if (this.minZoomReset) {
                    let imgDimensions = this.getImageDimensions()
                    this.imageNode.position({
                        x: imgDimensions.x,
                        y: imgDimensions.y,
                    })
                }
            } else {
                pos = {
                    x: (-((pointer.x - this.stage.x()) / currentScale) + pointer.x / scale) * scale,
                    y: (-((pointer.y - this.stage.y()) / currentScale) + pointer.y / scale) * scale,
                }
            }

            this.scale = scale
            this.stage.scale({ x: scale, y: scale })
            this.stage.position(pos)
            this.stage.batchDraw()
        },
        getPinchDistance(p1, p2) {
            return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2))
        },

        // ROTATION
        rotate() {
            if (this.imageNode.rotation() + 90 == 360) {
                this.imageNode.rotation(0)
            } else {
                this.imageNode.rotate(90)
            }
            this.configRotatedImage()
            this.stage.batchDraw()
        },
        configRotatedImage() {
            let imgDimensions = this.getImageDimensions()
            switch (this.imageNode.rotation()) {
                case 0:
                    this.imageNode.width(imgDimensions.width)
                    this.imageNode.height(imgDimensions.height)
                    this.imageNode.offsetX(0)
                    this.imageNode.offsetY(0)
                    break
                case 90: {
                    let oldWidth = this.imageNode.width()
                    this.imageNode.width(this.imageNode.height())
                    this.imageNode.height((this.imageNode.width() * this.imageNode.height()) / oldWidth)
                    this.imageNode.offsetX(0)
                    this.imageNode.offsetY(this.imageNode.width())
                    break
                }
                case 180:
                    this.imageNode.width(imgDimensions.width)
                    this.imageNode.height(imgDimensions.height)
                    this.imageNode.offsetX(this.imageNode.width())
                    break
                case 270: {
                    let oldWidth = this.imageNode.width()
                    this.imageNode.width(this.imageNode.height())
                    this.imageNode.height((this.imageNode.width() * this.imageNode.height()) / oldWidth)
                    this.imageNode.offsetX(this.imageNode.width())
                    this.imageNode.offsetY(-(this.imageNode.height() / 2))
                    break
                }
            }
        },

        // UTILS
        getImageDimensions() {
            let scale = 1
            let { clientWidth, clientHeight } = this.$refs.container
            let { width, height } = this.image
            if (width * scale > clientWidth) {
                scale = clientWidth / width
            }
            if (height * scale > clientHeight) {
                scale = clientHeight / height
            }

            return {
                scale,
                width,
                height,
                x: (clientWidth - width * scale) / 2,
                y: (clientHeight - height * scale) / 2,
            }
        },

        // SETUP
        async setupCanvas() {
            this.isLoading = true
            await this.loadImage()
            this.configStage()
            this.configImage()
            this.configZoom()
            this.isLoading = false
        },
    },
    async mounted() {
        await this.setupCanvas()
        window.addEventListener('resize', this.resize)
    },
}
</script>

<style lang="scss" scoped>
.custom-image-viewer {
    width: 100% !important;
    height: 100% !important;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
}
</style>
